import {useState} from 'react';
import {Button, Form, Modal, Spinner, Alert} from '@goproblems/ui';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {useChangeProblemLevelMutation} from '@api';
import {hideModal, useSelector, MODAL_CHANGE_LEVEL} from '@utils';
import {FIRST_LEVEL, LAST_KNOWN_LEVEL} from '@utils/const';
import {getShortLevelName} from '@utils/elo';

const generateLevelOptions = () => {
    const options: {value: number; label: string}[] = [];
    for (let level = FIRST_LEVEL; level <= LAST_KNOWN_LEVEL; level++) {
        options.push({value: level, label: getShortLevelName(level)});
    }
    return options;
};

const levelOptions = generateLevelOptions();

export const ChangeLevelModal = ({
    problemId,
    currentLevel,
    onSuccess,
}: {
    problemId: number;
    currentLevel?: string;
    onSuccess?: () => void;
}) => {
    const show = useSelector(s => s.modalChangeLevel);
    const {t} = useTranslation();
    const [changeProblemLevel, {isLoading}] = useChangeProblemLevelMutation();
    const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleClose = () => {
        hideModal(MODAL_CHANGE_LEVEL);
        setSelectedLevel(null);
        setError(null);
    };

    const handleSubmit = async () => {
        if (selectedLevel === null) {
            setError(t('change_level.please_select_level'));
            return;
        }

        try {
            await changeProblemLevel({
                problemId,
                level: selectedLevel,
            }).unwrap();
            toast.success(t('change_level.success_message'));
            handleClose();
            onSuccess?.();
        } catch (err) {
            setError(
                (err as {data?: {message?: string}})?.data?.message ??
                    t('change_level.error_changing_level')
            );
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {t('change_level.title', {id: problemId})}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {currentLevel && (
                    <div className="mb-3">
                        <strong>{t('change_level.current_level')}:</strong>{' '}
                        {currentLevel}
                    </div>
                )}

                {error && <Alert variant="danger">{error}</Alert>}

                <Form.Group>
                    <Form.Label>{t('change_level.new_level')}</Form.Label>
                    <Form.Select
                        value={selectedLevel ?? ''}
                        onChange={e => {
                            setSelectedLevel(
                                e.target.value ? Number(e.target.value) : null
                            );
                            setError(null);
                        }}
                        disabled={isLoading}>
                        <option value="">
                            {t('change_level.select_level')}
                        </option>
                        {levelOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="secondary"
                    onClick={handleClose}
                    disabled={isLoading}>
                    {t('common.cancel')}
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isLoading || selectedLevel === null}>
                    {isLoading ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            {t('change_level.changing')}
                        </>
                    ) : (
                        t('change_level.submit')
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
